import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Star, Calendar, MapPin } from "lucide-react";

const INITIALS = (name) => (name || "").split(" ").slice(-2).map((n) => n[0] || "").join("").toUpperCase();

const DoctorProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSlot, setSelectedSlot] = useState("");
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [useWallet, setUseWallet] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/doctors/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [id]);

    const grouped = useMemo(() => {
        const m = {};
        (data?.available_slots || []).forEach((s) => {
            const d = s.at.slice(0, 10);
            (m[d] = m[d] || []).push(s);
        });
        return m;
    }, [data]);

    const book = async () => {
        if (!user) {
            toast.error("Please sign in to book a consultation");
            navigate("/login");
            return;
        }
        if (!selectedSlot) { toast.error("Pick a slot"); return; }
        if (reason.length < 2) { toast.error("Add a brief reason"); return; }
        setSubmitting(true);
        try {
            const { data: r } = await api.post("/consultations", {
                doctor_id: id,
                scheduled_at: selectedSlot,
                mode: "chat",
                reason,
                patient_notes: notes,
                use_wallet: useWallet,
            });
            if (r.amount === 0) {
                toast.success("Consultation booked");
                navigate(`/consultations/${r.consultation_id}`);
            } else {
                toast.success("Redirecting to payment…");
                window.location.href = r.redirect_url;
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not book consultation");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !data) {
        return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Loading…</main></div>);
    }
    const d = data.doctor;
    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-4xl px-6 py-12 lg:px-12">
                <Link to="/consultations" className="text-xs text-zinc-500 hover:underline">← All doctors</Link>
                <section className="mt-4 grid gap-8 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                        <div className="flex flex-col items-center rounded-3xl border border-zinc-100 bg-white p-6 text-center dark:bg-white/[0.03] dark:border-white/10">
                            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-100 text-2xl font-semibold text-brand-700">{INITIALS(d.full_name)}</span>
                            <p className="mt-3 font-semibold text-zinc-900 dark:text-zinc-50">{d.full_name}</p>
                            <p className="text-xs text-zinc-500 capitalize">{d.specialization?.replace("-", " ")}</p>
                            <p className="mt-1 text-xs text-zinc-500">{d.qualifications}</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-xs"><Star className="h-3 w-3 fill-amber-400 text-amber-500" />{d.rating?.toFixed(1)} • {d.reviews_count} reviews</p>
                            <p className="mt-1 text-xs text-zinc-500">{d.experience_years}+ years experience</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-zinc-500"><MapPin className="h-3 w-3" />{d.hospital_affiliation}</p>
                            <p className="mt-4 text-2xl font-semibold text-zinc-900 dark:text-zinc-50">₹{d.consultation_fee}<span className="ml-1 text-xs text-zinc-500">/consult</span></p>
                        </div>
                    </div>

                    <div className="sm:col-span-2 space-y-6">
                        <div>
                            <p className="label-uppercase text-zinc-500">About</p>
                            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">{d.bio}</p>
                            <p className="mt-2 text-[11px] text-zinc-500">Languages: {(d.languages_spoken || []).join(", ") || "English"}</p>
                        </div>

                        <div>
                            <p className="label-uppercase text-zinc-500 flex items-center gap-1"><Calendar className="h-3 w-3" /> Pick a slot (next 7 days)</p>
                            <div className="mt-3 space-y-3" data-testid="doctor-slot-grid">
                                {Object.keys(grouped).length === 0 ? (
                                    <p className="text-xs text-zinc-500">No open slots in the next 7 days. Try again later.</p>
                                ) : Object.entries(grouped).map(([day, slots]) => (
                                    <div key={day}>
                                        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{new Date(day).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {slots.map((s) => (
                                                <button key={s.at} onClick={() => setSelectedSlot(s.at)} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${selectedSlot === s.at ? "border-brand-500 bg-brand-700 text-white" : "border-zinc-200 hover:bg-zinc-50 dark:hover:bg-white/5"}`} data-testid={`slot-${s.at}`}>
                                                    {s.label.split(" · ")[1]}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="label-uppercase text-zinc-500">Reason for consultation</label>
                            <input type="text" maxLength={240} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Persistent headache for 3 days" className="mt-2 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="consult-reason-input" />
                        </div>
                        <div>
                            <label className="label-uppercase text-zinc-500">Notes for doctor <span className="lowercase text-zinc-400">(optional)</span></label>
                            <textarea rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Symptoms, medications, allergies…" className="mt-2 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="consult-notes-input" />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-zinc-700 dark:text-zinc-300">
                            <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} data-testid="consult-use-wallet" />
                            Pay using wallet balance if available
                        </label>
                        <button onClick={book} disabled={submitting || !selectedSlot} className="btn-brand w-full disabled:opacity-50" data-testid="consult-book-btn">
                            {submitting ? "Processing…" : selectedSlot ? `Confirm & Pay ₹${d.consultation_fee}` : "Pick a slot to continue"}
                        </button>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default DoctorProfile;
